<div class="account-pages my-5 pt-5">
  <div class="container">
    <!-- Step Progress Indicator -->
    <div class="row justify-content-center mb-4">
      <div class="col-lg-10">
        <div class="card shadow-sm border-0" style="background: linear-gradient(135deg, #556ee6 0%, #6c81ec 100%);">
          <div class="card-body py-3 px-4">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <div>
                <small class="text-white-50 text-uppercase fw-semibold">Onboarding Progress</small>
                <h6 class="mb-0 mt-1">
                  <span class="text-white">Step {{ currentStep }} </span>
                  <span class="text-white">of {{ totalSteps }}</span>
                </h6>
              </div>
              <div class="text-end">
                <span class="badge bg-white text-primary font-size-12 px-3 py-2">
                  <i class="bx bx-building me-1"></i> Business Details
                </span>
              </div>
            </div>
            <div class="progress" style="height: 8px; border-radius: 10px; background-color: rgba(255, 255, 255, 0.2);">
              <div class="progress-bar bg-white" 
                   role="progressbar" 
                   [style.width.%]="progressPercentage"
                   [attr.aria-valuenow]="progressPercentage" 
                   aria-valuemin="0" 
                   aria-valuemax="100"
                   style="border-radius: 10px; transition: width 0.6s ease;">
              </div>
            </div>
            <div class="d-flex justify-content-between mt-2">
              <small class="text-white-50">
                <i class="bx bx-loader bx-spin me-1"></i>Business Profile Setup
              </small>
              <small class="text-white-50 font-size-11">{{ progressPercentage }}% Complete</small>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="row justify-content-center">
      <!-- Main Setup Card -->
      <div class="col-lg-8">
        <div class="card overflow-hidden shadow-sm border-0">
          <div class="bg-primary">
            <div class="text-primary text-center p-4">
              <div class="d-flex align-items-center justify-content-center">
                <i class="bx bx-building font-size-32 text-white me-3"></i>
                <div class="text-start">
                  <h5 class="text-white font-size-20 mb-1">Confirm WhatsApp Business Details</h5>
                  <p class="text-white-50 mb-0">These are the details users will see when you message them.</p>
                </div>
              </div>
            </div>
          </div>

          <div class="card-body p-4">
            @if (isLoading) {
              <div class="text-center py-5">
                <div class="spinner-border text-primary" role="status">
                  <span class="visually-hidden">Loading...</span>
                </div>
                <p class="mt-2 text-muted">Loading business profile...</p>
              </div>
            } @else {
              <div class="p-3">
                <div class="text-center mb-4">
                  <div class="avatar-lg mx-auto mb-3">
                    <div class="avatar-title bg-soft-primary text-primary rounded-circle">
                      <i class="bx bx-building font-size-24"></i>
                    </div>
                  </div>
                  <h5 class="mb-2">WhatsApp Business Profile</h5>
                  <p class="text-muted mb-0">
                    Complete your business profile to build trust with your community. This information will be visible to people who message you on WhatsApp.
                  </p>
                </div>

                <!-- Phone Number Indicator (multi-phone cycling) -->
                @if (hasMultiplePhones && currentPhone) {
                  <div class="alert alert-primary border-0 shadow-sm mb-4" role="alert">
                    <div class="d-flex align-items-center justify-content-between">
                      <div>
                        <h6 class="mb-1 font-size-14">
                          <i class="bx bxs-phone me-1"></i>
                          Phone {{ currentPhoneIndex + 1 }} of {{ allPhones.length }}
                        </h6>
                        <p class="mb-0 font-size-13">
                          Setting up profile for <strong>{{ currentPhone.verifiedName }}</strong> ({{ currentPhone.displayPhoneNumber }})
                        </p>
                      </div>
                    </div>
                    <!-- Phone progress pills -->
                    <div class="d-flex gap-2 mt-2">
                      @for (phone of allPhones; track phone.id; let i = $index) {
                        <span class="badge rounded-pill font-size-11 px-2 py-1"
                              [ngClass]="{
                                'bg-success': completedPhoneIds.has(phone.id),
                                'bg-primary': i === currentPhoneIndex && !completedPhoneIds.has(phone.id),
                                'bg-secondary': i !== currentPhoneIndex && !completedPhoneIds.has(phone.id)
                              }">
                          @if (completedPhoneIds.has(phone.id)) {
                            <i class="bx bx-check me-1"></i>
                          }
                          {{ phone.displayPhoneNumber }}
                        </span>
                      }
                    </div>
                  </div>
                } @else if (currentPhone) {
                  <div class="text-center mb-3">
                    <span class="text-muted font-size-13">
                      <i class="bx bxs-phone me-1"></i>
                      {{ currentPhone.verifiedName }} ({{ currentPhone.displayPhoneNumber }})
                    </span>
                  </div>
                }

                <div class="alert alert-info border-0 shadow-sm mb-4" role="alert">
                  <div class="d-flex align-items-start">
                    <div class="flex-shrink-0 me-3">
                      <i class="bx bx-info-circle font-size-20"></i>
                    </div>
                    <div class="flex-grow-1">
                      <h6 class="mb-1 font-size-14">Optional Step</h6>
                      <p class="mb-0 font-size-13">
                        You can skip this step and complete your business profile later in Organization Settings.
                      </p>
                    </div>
                  </div>
                </div>

                <!-- Business Profile Form -->
                <form [formGroup]="profileForm" (ngSubmit)="saveAndContinue()">
                  
                  <!-- Profile Picture -->
                  <div class="mb-4">
                    <label class="form-label fw-bold">
                      Profile Picture 
                      <small class="text-muted">(Optional - max 5MB, JPEG/PNG)</small>
                    </label>
                    <div class="d-flex align-items-center">
                      @if (previewUrl) {
                        <div class="me-3">
                          <img [src]="previewUrl" alt="Profile Preview" class="rounded-circle" style="width: 80px; height: 80px; object-fit: cover; border: 3px solid #f0f0f0;">
                        </div>
                      }
                      <div class="flex-grow-1">
                        <input 
                          type="file" 
                          accept="image/jpeg,image/png" 
                          (change)="onFileSelected($event)"
                          class="form-control"
                          id="profilePicture">
                        <small class="text-muted d-block mt-1">
                          <i class="bx bx-info-circle me-1"></i>Recommended: Square image, at least 192x192 pixels
                        </small>
                      </div>
                    </div>
                  </div>

                  <!-- About -->
                  <div class="mb-4">
                    <label for="about" class="form-label fw-bold">
                      About 
                      <small class="text-muted">(max 139 characters)</small>
                    </label>
                    <input 
                      type="text" 
                      class="form-control" 
                      id="about"
                      formControlName="about"
                      placeholder="Brief description of your organization"
                      maxlength="139">
                    <div class="d-flex justify-content-between mt-1">
                      <small class="text-muted">
                        <i class="bx bx-info-circle me-1"></i>Example: "A vibrant community church in downtown"
                      </small>
                      <small class="text-muted">{{ getRemainingChars('about', 139) }} remaining</small>
                    </div>
                  </div>

                  <!-- Description -->
                  <div class="mb-4">
                    <label for="description" class="form-label fw-bold">
                      Description 
                      <small class="text-muted">(max 512 characters)</small>
                    </label>
                    <textarea 
                      class="form-control" 
                      id="description"
                      formControlName="description"
                      rows="4"
                      placeholder="Detailed description of your organization"
                      maxlength="512"></textarea>
                    <div class="d-flex justify-content-between mt-1">
                      <small class="text-muted">
                        <i class="bx bx-info-circle me-1"></i>Provide more details about your mission and activities
                      </small>
                      <small class="text-muted">{{ getRemainingChars('description', 512) }} remaining</small>
                    </div>
                  </div>

                  <!-- Industry -->
                  <div class="mb-4">
                    <label for="vertical" class="form-label fw-bold">Industry</label>
                    <select class="form-select" id="vertical" formControlName="vertical">
                      @for (option of verticalOptions; track option.value) {
                        <option [value]="option.value">{{ option.label }}</option>
                      }
                    </select>
                    <small class="text-muted d-block mt-1">
                      <i class="bx bx-info-circle me-1"></i>Select the industry that best describes your organization
                    </small>
                  </div>

                  <!-- Address -->
                  <div class="mb-4">
                    <label for="address" class="form-label fw-bold">
                      Address 
                      <small class="text-muted">(max 256 characters)</small>
                    </label>
                    <gmp-place-autocomplete 
                      #addressInput
                      class="form-control"
                      [attr.placeholder]="'Start typing your business address...'"
                      type="address"
                      style="width: 100%; border: none; outline: none;">
                    </gmp-place-autocomplete>
                    <input 
                      type="hidden"
                      formControlName="address">
                    <small class="text-muted d-block mt-1">
                      <i class="bx bx-map me-1"></i>Start typing to search for your address
                    </small>
                  </div>

                  <!-- Email -->
                  <div class="mb-4">
                    <label for="email" class="form-label fw-bold">
                      Email 
                      <small class="text-muted">(max 128 characters)</small>
                    </label>
                    <input 
                      type="email" 
                      class="form-control" 
                      id="email"
                      formControlName="email"
                      placeholder="contact@example.com"
                      maxlength="128"
                      [class.is-invalid]="profileForm.get('email')?.invalid && profileForm.get('email')?.touched">
                    @if (profileForm.get('email')?.invalid && profileForm.get('email')?.touched) {
                      <div class="invalid-feedback">
                        Please enter a valid email address
                      </div>
                    }
                    <small class="text-muted d-block mt-1">
                      <i class="bx bx-info-circle me-1"></i>Contact email for your organization
                    </small>
                  </div>

                  <!-- Website 1 -->
                  <div class="mb-4">
                    <label for="website1" class="form-label fw-bold">
                      Website 1 
                      <small class="text-muted">(max 256 characters)</small>
                    </label>
                    <input 
                      type="url" 
                      class="form-control" 
                      id="website1"
                      formControlName="website1"
                      placeholder="https://example.com"
                      maxlength="256">
                    <small class="text-muted d-block mt-1">
                      <i class="bx bx-info-circle me-1"></i>Must start with http:// or https://
                    </small>
                  </div>

                  <!-- Website 2 -->
                  <div class="mb-4">
                    <label for="website2" class="form-label fw-bold">
                      Website 2 
                      <small class="text-muted">(max 256 characters - Optional)</small>
                    </label>
                    <input 
                      type="url" 
                      class="form-control" 
                      id="website2"
                      formControlName="website2"
                      placeholder="https://example.com"
                      maxlength="256">
                    <small class="text-muted d-block mt-1">
                      <i class="bx bx-info-circle me-1"></i>Additional website URL (optional)
                    </small>
                  </div>

                  <!-- Action Buttons -->
                  <div class="row mt-4">
                    <div class="col-md-6 mb-3 mb-md-0">
                      <button 
                        type="button" 
                        class="btn btn-outline-secondary w-100 waves-effect"
                        (click)="skip()"
                        [disabled]="isSaving">
                        @if (hasMultiplePhones && !isLastPhone) {
                          Skip All — I'll Complete This Later
                        } @else {
                          Skip — I'll Complete This Later
                        }
                      </button>
                    </div>
                    <div class="col-md-6">
                      <button 
                        type="submit" 
                        class="btn btn-primary w-100 waves-effect waves-light"
                        [disabled]="isSaving">
                        @if (isSaving) {
                          <span><i class="bx bx-loader bx-spin me-2"></i>Saving...</span>
                        } @else if (hasMultiplePhones && !isLastPhone) {
                          <span><i class="bx bx-check me-2"></i>Save & Next Phone</span>
                        } @else {
                          <span><i class="bx bx-check me-2"></i>Save & Continue</span>
                        }
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            }
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
